// The backup e-mail for the sign-in code (issue #23), shown in the account section of the dashboard: typed, then
// confirmed by a 6-digit code sent to it, so a sign-in code only ever goes to an address its owner reads. Built for
// the former account page and mounted in `Dashboard.tsx` when the dashboard shell of issue #10 replaced it.
import { useEffect, useState } from 'react';
import type { Lang, UiCopy } from '@ringring/core';
import { api, type ApiError } from './api';
import { Filled } from './lang';
import { problemOf, problemText, type Problem } from './SignIn';

type EmailMode = { kind: 'view' } | { kind: 'edit' } | { kind: 'confirm'; email: string; sentAt: number; resendAfter: number };

export interface BackupEmailProps {
  t: UiCopy;
  lang: Lang;
  email: string | null;
  /** Called with the address once confirmed, or null once removed. */
  onChange: (email: string | null) => void;
}

/**
 * The backup address for the sign-in code: typed, then confirmed by a 6-digit code sent to it, so a sign-in code
 * only ever goes to an address its owner reads. Shown in full to its owner; the sign-in page shows it masked.
 */
export function BackupEmail({ t, lang, email, onChange }: BackupEmailProps) {
  const [mode, setMode] = useState<EmailMode>({ kind: email ? 'view' : 'edit' });
  const [typed, setTyped] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // Tick every second while a countdown is on screen (resend timer, lockout, cooldown).
  const counting = mode.kind === 'confirm' || problem?.code === 'locked' || problem?.code === 'cooldown';
  useEffect(() => {
    if (!counting) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [counting]);

  const locked = problem?.code === 'locked' && now < problem.until;
  const resendWait = mode.kind === 'confirm' ? Math.max(0, Math.ceil((mode.sentAt + mode.resendAfter * 1000 - now) / 1000)) : 0;
  const message = problem ? problemText(problem, lang, now) : null;

  const run = async (f: () => Promise<void>) => {
    setProblem(null);
    setNotice(null);
    setBusy(true);
    try {
      await f();
    } catch (err) {
      setNow(Date.now());
      setProblem(problemOf(err as ApiError));
    } finally {
      setBusy(false);
    }
  };

  const sendCode = (address: string) =>
    run(async () => {
      const r = await api<{ email: string; resend_after: number }>('/account/email/code', { method: 'POST', body: JSON.stringify({ email: address, lang }) });
      setMode({ kind: 'confirm', email: r.email, sentAt: Date.now(), resendAfter: r.resend_after });
      setCode('');
    });

  const confirm = () =>
    run(async () => {
      const r = await api<{ email: string }>('/account/email', { method: 'POST', body: JSON.stringify({ code, lang }) });
      onChange(r.email);
      setMode({ kind: 'view' });
      setNotice(t.email_confirmed);
      setCode('');
      setTyped('');
    });

  const remove = () =>
    run(async () => {
      await api('/account/email', { method: 'DELETE' });
      onChange(null);
      setMode({ kind: 'edit' });
      setNotice(t.email_removed);
    });

  const alert = message ? (
    <p id="email-message" role="alert">
      {message}
    </p>
  ) : null;
  const status = notice ? <p role="status">{notice}</p> : null;
  const describedBy = message ? 'email-message' : undefined;

  return (
    <section className="card" aria-labelledby="backup-email">
      <h2 id="backup-email">{t.backup_email}</h2>
      <p className="muted">{t.backup_email_intro}</p>
      {mode.kind === 'view' ? (
        <>
          <p>{email ? <bdi dir="ltr">{email}</bdi> : t.backup_email_none}</p>
          {status}
          {alert}
          <div className="row">
            <button className="btn secondary" type="button" onClick={() => setMode({ kind: 'edit' })} disabled={busy}>
              {email ? t.change_email : t.send_email_code}
            </button>
            {email ? (
              <button className="btn secondary" type="button" onClick={() => void remove()} disabled={busy}>
                {t.remove_email}
              </button>
            ) : null}
          </div>
        </>
      ) : mode.kind === 'edit' ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void sendCode(typed);
          }}
        >
          <label htmlFor="email">{t.email_prompt}</label>
          <input id="email" type="email" inputMode="email" autoComplete="email" dir="ltr" value={typed} onChange={(e) => setTyped(e.target.value)} disabled={locked || busy} aria-describedby={describedBy} required />
          {status}
          {alert}
          <div className="row">
            <button className="btn" type="submit" disabled={locked || busy}>
              {t.send_email_code}
            </button>
            {email ? (
              <button
                className="btn secondary"
                type="button"
                onClick={() => {
                  setMode({ kind: 'view' });
                  setProblem(null);
                }}
              >
                {t.cancel}
              </button>
            ) : null}
          </div>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void confirm();
          }}
        >
          <p>
            <Filled template={t.email_code_sent} vars={{ email: mode.email }} />
          </p>
          <p className="muted">{t.email_hint}</p>
          <label htmlFor="email-code">{t.code_prompt_email}</label>
          <input id="email-code" className="code" inputMode="numeric" autoComplete="one-time-code" dir="ltr" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} disabled={locked || busy} aria-describedby={describedBy} autoFocus />
          {alert}
          <div className="row">
            <button className="btn" type="submit" disabled={locked || busy || code.replace(/\s/g, '').length < 6}>
              {t.confirm_email}
            </button>
            <button className="btn secondary" type="button" onClick={() => void sendCode(mode.email)} disabled={locked || busy || resendWait > 0}>
              {resendWait > 0 ? <Filled template={t.resend_in} vars={{ seconds: String(resendWait) }} /> : t.resend_code}
            </button>
            <button
              className="btn secondary"
              type="button"
              onClick={() => {
                setMode(email ? { kind: 'view' } : { kind: 'edit' });
                setProblem(null);
                setCode('');
              }}
            >
              {t.cancel}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
